import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Upload, Search, User, MapPin, Calendar, ExternalLink, 
  AlertCircle, Loader2, Image as ImageIcon,
  RefreshCw, Database, Eye, Shield, Camera, FileText
} from 'lucide-react'
import { format } from 'date-fns'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

interface DetectedFace {
  x: number
  y: number
  width: number
  height: number
  lm1_x: number
  lm1_y: number
  lm2_x: number
  lm2_y: number
  lm3_x: number
  lm3_y: number
  lm4_x: number
  lm4_y: number
  lm5_x: number
  lm5_y: number
}

interface ProfileMatch {
  score: number
  face_url: string
  profile_url: string
  photo_url: string
  source_url: string
  age: number | null
  first_name: string
  last_name: string
  maiden_name: string | null
  city: string | null
  country: string | null
  bounding_box: {
    x: number
    y: number
    width: number
    height: number
  }
}

interface SearchResult {
  face_index: number
  face_data: DetectedFace
  matches_by_source: Record<string, {
    profiles: ProfileMatch[]
    total_matches: number
    source_database?: string
    error?: string
  }>
}

interface ReverseSearchResponse {
  success: boolean
  message?: string
  faces_detected: number
  image_id?: string
  results: SearchResult[]
  total_matches: number
  sources_searched: string[]
}

interface RateLimitInfo {
  api_key_masked: string | null
  limit: number
  remaining: number
  end_date: string | null
  speed: number
  allowed_methods: string[]
  disabled: boolean
}

/** PimEyes single result item from get_results (supports camelCase and snake_case) */
interface PimEyesResultItem {
  id?: string
  hash?: string
  imageUrl?: string
  thumbnailUrl?: string
  sourceUrl?: string
  image_url?: string
  thumbnail_url?: string
  source_url?: string
  domain?: string
  quality?: number
  width?: number
  height?: number
  adultContent?: boolean
  locked?: boolean
}

function getPimeyesImageUrl(item: PimEyesResultItem): string | undefined {
  return item.thumbnailUrl || item.imageUrl || item.thumbnail_url || item.image_url
}

function getPimeyesSourceUrl(item: PimEyesResultItem): string | undefined {
  return item.sourceUrl || item.source_url
}

/** PimEyes reverse-search API response */
interface PimEyesSearchResponse {
  success: boolean
  error?: string
  hash?: string
  number_of_results?: number
  is_more_results?: boolean
  results: PimEyesResultItem[]
}

const DATABASE_OPTIONS = [
  { id: 'vk_wall', name: 'VK Wall Photos', description: 'Photos from VK wall posts', icon: '📷' },
  { id: 'vkok_avatar', name: 'VK/OK Avatars', description: 'Profile photos from VK and Odnoklassniki', icon: '👤' },
  { id: 'tt_avatar', name: 'TikTok Avatars', description: 'Profile photos from TikTok', icon: '🎵' },
  { id: 'ch_avatar', name: 'Clubhouse Avatars', description: 'Profile photos from Clubhouse', icon: '🎙️' },
  { id: 'vkokn_avatar', name: 'VK/OK New Avatars', description: 'Newer profile photos from VK/OK', icon: '🆕' },
  { id: 'sb_photo', name: 'Social Blade', description: 'Photos from Social Blade database', icon: '📊' },
]

const SEARCH_PROVIDERS = [
  { id: 'search4faces' as const, name: 'VK/Tiktok lookup', description: 'Social DBs (VK, TikTok, etc.)', icon: Database },
  { id: 'pimeyes' as const, name: 'Advanced facial search', description: 'Web face search', icon: Eye },
]

export default function FacialRecognition() {
  const [searchProvider, setSearchProvider] = useState<'search4faces' | 'pimeyes'>('search4faces')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [selectedDatabases, setSelectedDatabases] = useState<string[]>(['vk_wall'])
  const [includeHidden, setIncludeHidden] = useState(true)
  const [maxResults, setMaxResults] = useState(50)
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<ReverseSearchResponse | null>(null)
  const [pimeyesResults, setPimeyesResults] = useState<PimEyesSearchResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [rateLimit, setRateLimit] = useState<RateLimitInfo | null>(null)
  const [loadingRateLimit, setLoadingRateLimit] = useState(false)
  const [generatingReport, setGeneratingReport] = useState(false)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file) {
      setSelectedFile(file)
      setPreviewUrl(URL.createObjectURL(file))
      setSearchResults(null)
      setPimeyesResults(null)
      setError(null)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: false
  })

  const fetchRateLimit = async () => {
    setLoadingRateLimit(true)
    try {
      const response = await fetch('/api/facial-recognition/rate-limit', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })
      if (response.ok) {
        const data = await response.json()
        setRateLimit(data)
      }
    } catch (err) {
      console.error('Failed to fetch rate limit:', err)
    } finally {
      setLoadingRateLimit(false)
    }
  }

  const handleSearch = async () => {
    if (!selectedFile) {
      setError('Please select an image first')
      return
    }

    if (searchProvider === 'search4faces' && selectedDatabases.length === 0) {
      setError('Please select at least one database to search')
      return
    }

    setIsSearching(true)
    setError(null)
    setSearchResults(null)
    setPimeyesResults(null)

    try {
      if (searchProvider === 'pimeyes') {
        const formData = new FormData()
        formData.append('image', selectedFile)
        formData.append('limit', String(maxResults))

        const response = await fetch('/api/facial-recognition/pimeyes/reverse-search', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: formData
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          const msg = errorData.detail ?? (response.status === 503
            ? 'PimEyes is unavailable. It may not be configured (set PIMEYES_EMAIL and PIMEYES_PASSWORD on the server) or login failed.'
            : 'PimEyes search failed')
          throw new Error(msg)
        }

        const data: PimEyesSearchResponse = await response.json()
        setPimeyesResults(data)
      } else {
        const formData = new FormData()
        formData.append('image', selectedFile)
        formData.append('sources', selectedDatabases.join(','))
        formData.append('include_hidden', String(includeHidden))
        formData.append('max_results', String(maxResults))
        formData.append('language', 'en')

        const response = await fetch('/api/facial-recognition/reverse-search', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: formData
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.detail || 'Search failed')
        }

        const data: ReverseSearchResponse = await response.json()
        setSearchResults(data)
        fetchRateLimit()
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during the search')
    } finally {
      setIsSearching(false)
    }
  }

  const toggleDatabase = (dbId: string) => {
    setSelectedDatabases(prev => 
      prev.includes(dbId) 
        ? prev.filter(id => id !== dbId)
        : [...prev, dbId]
    )
  }

  const getScoreBadgeColor = (score: number) => {
    if (score >= 90) return 'bg-green-500/20 text-green-400 border-green-500/50'
    if (score >= 70) return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
    if (score >= 50) return 'bg-orange-500/20 text-orange-400 border-orange-500/50'
    return 'bg-red-500/20 text-red-400 border-red-500/50'
  }

  const generatePDFReport = async () => {
    if (!previewUrl && !pimeyesResults?.results?.length && !searchResults) {
      setError('No results to generate report')
      return
    }
    setGeneratingReport(true)
    try {
      const reportEl = document.createElement('div')
      reportEl.style.cssText = 'width:210mm;padding:20mm;background:#fff;font-family:Arial,sans-serif;position:fixed;left:-9999px;top:0;z-index:-1'
      document.body.appendChild(reportEl)

      const providerLabel = searchProvider === 'pimeyes' ? 'Advanced facial search' : 'VK/Tiktok lookup'
      const resultCount = pimeyesResults?.results?.length ?? searchResults?.total_matches ?? 0

      let resultsHtml = ''
      if (pimeyesResults?.success && pimeyesResults.results?.length) {
        resultsHtml = pimeyesResults.results.map((item, i) => {
          const imgUrl = getPimeyesImageUrl(item)
          const srcUrl = getPimeyesSourceUrl(item) || '#'
          const quality = item.quality != null ? `${Math.round(item.quality)}%` : ''
          const domain = item.domain || ''
          return `
            <div style="display:inline-block;width:30%;margin:8px;vertical-align:top;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
              <img src="${imgUrl || ''}" alt="Match ${i + 1}" style="width:100%;height:120px;object-fit:cover;" onerror="this.style.background='#f3f4f6';this.alt='Image unavailable'"/>
              <div style="padding:8px;font-size:11px;">
                ${quality ? `<span style="font-weight:bold;color:#059669;">${quality} match</span><br/>` : ''}
                ${domain ? `<span style="color:#6b7280;">${domain}</span><br/>` : ''}
                <a href="${srcUrl}" style="color:#059669;word-break:break-all;">${srcUrl}</a>
              </div>
            </div>
          `
        }).join('')
      } else if (searchResults?.results?.length) {
        resultsHtml = searchResults.results.flatMap((fr) =>
          Object.entries(fr.matches_by_source).flatMap(([, data]) =>
            (data.profiles || []).slice(0, 5).map((p) => `
              <div style="display:inline-block;width:30%;margin:8px;vertical-align:top;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
                <img src="${p.face_url || ''}" alt="Match" style="width:100%;height:120px;object-fit:cover;" onerror="this.style.background='#f3f4f6'"/>
                <div style="padding:8px;font-size:11px;">
                  <span style="font-weight:bold;">${p.first_name} ${p.last_name}</span> ${p.score ? `(${p.score.toFixed(0)}%)` : ''}<br/>
                  <a href="${p.source_url}" style="color:#059669;word-break:break-all;">${p.source_url}</a>
                </div>
              </div>
            `)
          )
        ).join('')
      }

      reportEl.innerHTML = `
        <div style="border-bottom:3px solid #10b981;padding-bottom:20px;margin-bottom:24px;">
          <div style="display:flex;align-items:center;gap:15px;margin-bottom:10px;">
            <div style="width:60px;height:60px;background:linear-gradient(135deg,#10b981,#059669);border-radius:12px;color:white;font-size:28px;font-weight:bold;display:flex;align-items:center;justify-content:center;">S</div>
            <div>
              <h1 style="margin:0;font-size:28px;font-weight:bold;color:#1f2937;">Sycek OSINT</h1>
              <p style="margin:5px 0 0 0;color:#6b7280;font-size:14px;">Facial Recognition Intelligence Report</p>
            </div>
          </div>
          <div style="font-size:12px;color:#6b7280;">
            Generated: ${format(new Date(), 'PPpp')} | Provider: ${providerLabel} | Results: ${resultCount}
          </div>
        </div>
        ${previewUrl ? `
        <div style="margin-bottom:24px;">
          <h3 style="margin:0 0 12px 0;font-size:14px;color:#374151;">Search Image</h3>
          <img src="${previewUrl}" alt="Query" style="max-width:300px;max-height:300px;border:1px solid #e5e7eb;border-radius:8px;"/>
        </div>
        ` : ''}
        <h3 style="margin:0 0 12px 0;font-size:14px;color:#374151;">Match Results</h3>
        <div style="margin-top:12px;">${resultsHtml || '<p style="color:#6b7280;">No results to display.</p>'}</div>
      `

      await new Promise(r => setTimeout(r, 800))

      const canvas = await html2canvas(reportEl, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      })
      document.body.removeChild(reportEl)

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4')
      const imgWidth = 210
      const pageHeight = 297
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      let heightLeft = imgHeight
      let position = 0

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight

      while (heightLeft > 0) {
        position = heightLeft - imgHeight
        pdf.addPage()
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
        heightLeft -= pageHeight
      }

      const totalPages = pdf.getNumberOfPages()
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i)
        pdf.setFontSize(8)
        pdf.setTextColor(100, 100, 100)
        pdf.text(
          `Sycek OSINT - Confidential - Page ${i} of ${totalPages}`,
          pdf.internal.pageSize.getWidth() / 2,
          pdf.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        )
      }

      pdf.save(`Sycek_Facial_Report_${format(new Date(), 'yyyy-MM-dd_HHmm')}.pdf`)
    } catch (err) {
      console.error('Report generation error:', err)
      setError('Failed to generate PDF report. Please try again.')
    } finally {
      setGeneratingReport(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Camera className="w-8 h-8 text-green-400" />
              Facial Recognition & Reverse Photo Lookup
            </h1>
            <p className="text-gray-400 mt-2">
              Upload an image to detect faces and search for matching profiles across social media platforms
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchRateLimit}
            disabled={loadingRateLimit}
            className="border-green-500/30 text-green-400 hover:bg-green-500/10"
          >
            {loadingRateLimit ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Check API Status
          </Button>
        </div>

        {/* Rate Limit Info */}
        {rateLimit && (
          <Card className="bg-black/50 border border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Database className="w-5 h-5 text-green-400" />
                    <span className="text-gray-400">API Status:</span>
                    <Badge className={rateLimit.disabled ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}>
                      {rateLimit.disabled ? 'Disabled' : 'Active'}
                    </Badge>
                  </div>
                  <div className="text-gray-400">
                    Remaining: <span className="text-green-400 font-mono">{rateLimit.remaining.toLocaleString()}</span> / {rateLimit.limit.toLocaleString()}
                  </div>
                  <div className="text-gray-400">
                    Rate: <span className="text-green-400 font-mono">{rateLimit.speed}</span> req/min
                  </div>
                </div>
                {rateLimit.end_date && (
                  <div className="text-gray-500 text-sm">
                    Valid until: {new Date(rateLimit.end_date).toLocaleDateString()}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Provider selector */}
        <Card className="bg-black/50 border border-green-500/30">
          <CardContent className="p-4">
            <Label className="text-gray-400 block mb-3">Search provider</Label>
            <div className="flex flex-wrap gap-3">
              {SEARCH_PROVIDERS.map((p) => {
                const Icon = p.icon
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setSearchProvider(p.id)
                      setSearchResults(null)
                      setPimeyesResults(null)
                      setError(null)
                    }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${
                      searchProvider === p.id
                        ? 'border-green-500 bg-green-500/20 text-green-400'
                        : 'border-green-500/30 text-gray-400 hover:border-green-500/50 hover:bg-green-500/5'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <div className="text-left">
                      <p className="font-medium text-white">{p.name}</p>
                      <p className="text-xs opacity-80">{p.description}</p>
                    </div>
                  </button>
                )
              })}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Upload & Settings */}
          <div className="space-y-6">
            {/* Image Upload */}
            <Card className="bg-black/50 border border-green-500/30">
              <CardHeader>
                <CardTitle className="text-green-400 flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Upload Image
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Upload a JPEG or PNG image (max 10MB)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
                    isDragActive 
                      ? 'border-green-400 bg-green-500/10' 
                      : 'border-green-500/30 hover:border-green-500/50 hover:bg-green-500/5'
                  }`}
                >
                  <input {...getInputProps()} />
                  {previewUrl ? (
                    <div className="space-y-4">
                      <img 
                        src={previewUrl} 
                        alt="Preview" 
                        className="max-h-48 mx-auto rounded-lg border border-green-500/30"
                      />
                      <p className="text-gray-400 text-sm">{selectedFile?.name}</p>
                      <p className="text-green-400 text-xs">Click or drag to replace</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <ImageIcon className="w-12 h-12 mx-auto text-green-500/50" />
                      <div>
                        <p className="text-gray-300">Drag & drop an image here</p>
                        <p className="text-gray-500 text-sm">or click to select</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Database Selection (Search4Faces only) */}
            {searchProvider === 'search4faces' && (
              <Card className="bg-black/50 border border-green-500/30">
                <CardHeader>
                  <CardTitle className="text-green-400 flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    Search Databases
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Select which databases to search
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {DATABASE_OPTIONS.map((db) => (
                    <div
                      key={db.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedDatabases.includes(db.id)
                          ? 'border-green-500/50 bg-green-500/10'
                          : 'border-green-500/20 hover:border-green-500/30'
                      }`}
                      onClick={() => toggleDatabase(db.id)}
                    >
                      <Checkbox 
                        checked={selectedDatabases.includes(db.id)}
                        onCheckedChange={() => toggleDatabase(db.id)}
                        className="border-green-500/50"
                      />
                      <span className="text-xl">{db.icon}</span>
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{db.name}</p>
                        <p className="text-gray-500 text-xs">{db.description}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Search Options */}
            <Card className="bg-black/50 border border-green-500/30">
              <CardHeader>
                <CardTitle className="text-green-400 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Search Options
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {searchProvider === 'search4faces' && (
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-300">Include Hidden Profiles</Label>
                    <Checkbox 
                      checked={includeHidden}
                      onCheckedChange={(checked) => setIncludeHidden(!!checked)}
                      className="border-green-500/50"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-gray-300">
                    {searchProvider === 'pimeyes' ? 'Max results' : 'Max Results per Database'}
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    max={searchProvider === 'pimeyes' ? 250 : 500}
                    value={maxResults}
                    onChange={(e) =>
                      setMaxResults(
                        Math.min(searchProvider === 'pimeyes' ? 250 : 500, Math.max(1, parseInt(e.target.value) || 50))
                      )
                    }
                    className="bg-black/50 border-green-500/30 text-white"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Search Button */}
            <Button
              onClick={handleSearch}
              disabled={
                !selectedFile ||
                (searchProvider === 'search4faces' && selectedDatabases.length === 0) ||
                isSearching
              }
              className="w-full bg-green-500 hover:bg-green-600 text-black font-semibold py-6 text-lg"
            >
              {isSearching ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5 mr-2" />
                  Search for Matches
                </>
              )}
            </Button>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
          </div>

          {/* Right Panel - Results */}
          <div className="lg:col-span-2">
            <Card className="bg-black/50 border border-green-500/30 h-full">
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-green-400 flex items-center gap-2">
                      <Eye className="w-5 h-5" />
                      Search Results
                    </CardTitle>
                    {searchResults && (
                      <CardDescription className="text-gray-400">
                        Found {searchResults.total_matches} matches across {searchResults.faces_detected} detected face(s)
                      </CardDescription>
                    )}
                    {pimeyesResults && pimeyesResults.success && (
                      <CardDescription className="text-gray-400">
                        {pimeyesResults.number_of_results ?? pimeyesResults.results?.length ?? 0} result(s)
                      </CardDescription>
                    )}
                  </div>
                  {(pimeyesResults?.success && pimeyesResults.results?.length) || (searchResults && searchResults.total_matches > 0) ? (
                    <Button
                      onClick={generatePDFReport}
                      disabled={generatingReport}
                      variant="outline"
                      className="border-green-500/30 text-green-400 hover:bg-green-500/10 shrink-0 flex items-center gap-2"
                    >
                      {generatingReport ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <FileText className="w-4 h-4" />
                      )}
                      {generatingReport ? 'Generating...' : 'Generate Report'}
                    </Button>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent>
                {!searchResults && !pimeyesResults && !isSearching && (
                  <div className="text-center py-20">
                    <Search className="w-16 h-16 mx-auto text-green-500/20 mb-4" />
                    <p className="text-gray-500">Upload an image and click search to find matching profiles</p>
                  </div>
                )}

                {isSearching && (
                  <div className="text-center py-20">
                    <Loader2 className="w-16 h-16 mx-auto text-green-400 animate-spin mb-4" />
                    <p className="text-gray-400">
                      {searchProvider === 'pimeyes' ? 'Searching web sources...' : 'Searching social databases...'}
                    </p>
                    <p className="text-gray-500 text-sm mt-2">This may take a moment</p>
                  </div>
                )}

                {/* PimEyes results */}
                {pimeyesResults && !isSearching && (
                  <div className="space-y-6">
                    {!pimeyesResults.success ? (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <p className="text-red-400">{pimeyesResults.error || 'PimEyes search failed'}</p>
                      </div>
                    ) : (pimeyesResults.results?.length === 0 ? (
                      <div className="text-center py-10 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                        <AlertCircle className="w-12 h-12 mx-auto text-yellow-400 mb-4" />
                        <p className="text-yellow-400">No matches found</p>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                            <p className="text-3xl font-bold text-green-400">
                              {pimeyesResults.number_of_results ?? pimeyesResults.results?.length ?? 0}
                            </p>
                            <p className="text-gray-400 text-sm">PimEyes matches</p>
                          </div>
                          {pimeyesResults.is_more_results && (
                            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                              <p className="text-lg font-medium text-green-400">More available</p>
                              <p className="text-gray-400 text-sm">Increase limit to fetch more</p>
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[60vh] overflow-y-auto">
                          {pimeyesResults.results.map((item, idx) => {
                            const imageUrl = getPimeyesImageUrl(item)
                            const sourceUrl = getPimeyesSourceUrl(item)
                            return (
                              <div
                                key={item.id ?? idx}
                                className="bg-black/30 border border-green-500/20 rounded-lg p-3 hover:border-green-500/40 transition-all"
                              >
                                {sourceUrl ? (
                                  <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="block">
                                    <img
                                      src={imageUrl || '/placeholder-face.png'}
                                      alt="Match"
                                      className="w-full aspect-square object-cover rounded-lg border border-green-500/30 mb-2"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).src = '/placeholder-face.png'
                                      }}
                                    />
                                  </a>
                                ) : (
                                  <img
                                    src={imageUrl || '/placeholder-face.png'}
                                    alt="Match"
                                    className="w-full aspect-square object-cover rounded-lg border border-green-500/30 mb-2"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = '/placeholder-face.png'
                                    }}
                                  />
                                )}
                                <div className="flex items-center justify-between gap-2">
                                  {item.quality != null && (
                                    <Badge className={getScoreBadgeColor(item.quality)}>
                                      {item.quality.toFixed(0)}%
                                    </Badge>
                                  )}
                                  {item.domain && (
                                    <span className="text-gray-500 text-xs truncate" title={item.domain}>
                                      {item.domain}
                                    </span>
                                  )}
                                </div>
                                {sourceUrl && (
                                  <a
                                    href={sourceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-2 text-green-400 hover:text-green-300 text-xs flex items-center gap-1"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    View source
                                  </a>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </>
                    ))}
                  </div>
                )}

                {searchResults && !pimeyesResults && (
                  <div className="space-y-6">
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-green-400">{searchResults.faces_detected}</p>
                        <p className="text-gray-400 text-sm">Faces Detected</p>
                      </div>
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-green-400">{searchResults.total_matches}</p>
                        <p className="text-gray-400 text-sm">Total Matches</p>
                      </div>
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-green-400">{searchResults.sources_searched.length}</p>
                        <p className="text-gray-400 text-sm">Databases Searched</p>
                      </div>
                    </div>

                    {searchResults.faces_detected === 0 ? (
                      <div className="text-center py-10 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                        <AlertCircle className="w-12 h-12 mx-auto text-yellow-400 mb-4" />
                        <p className="text-yellow-400">No faces detected in the image</p>
                        <p className="text-gray-500 text-sm mt-2">Try uploading a clearer image with visible faces</p>
                      </div>
                    ) : (
                      <Tabs defaultValue="face-0" className="w-full">
                        <TabsList className="bg-black/50 border border-green-500/30 w-full justify-start overflow-x-auto">
                          {searchResults.results.map((_, idx) => (
                            <TabsTrigger
                              key={idx}
                              value={`face-${idx}`}
                              className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400"
                            >
                              Face {idx + 1}
                            </TabsTrigger>
                          ))}
                        </TabsList>

                        {searchResults.results.map((faceResult, faceIdx) => (
                          <TabsContent key={faceIdx} value={`face-${faceIdx}`} className="mt-4">
                            <div className="space-y-4">
                              {Object.entries(faceResult.matches_by_source).map(([source, sourceResult]) => (
                                <div key={source} className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <h4 className="text-white font-medium flex items-center gap-2">
                                      <Database className="w-4 h-4 text-green-400" />
                                      {DATABASE_OPTIONS.find(d => d.id === source)?.name || source}
                                    </h4>
                                    <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                                      {sourceResult.total_matches} matches
                                    </Badge>
                                  </div>

                                  {sourceResult.error ? (
                                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                                      <p className="text-red-400 text-sm">{sourceResult.error}</p>
                                    </div>
                                  ) : sourceResult.profiles.length === 0 ? (
                                    <div className="bg-gray-500/10 border border-gray-500/30 rounded-lg p-4 text-center">
                                      <p className="text-gray-500">No matches found in this database</p>
                                    </div>
                                  ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                                      {sourceResult.profiles.map((profile, profileIdx) => (
                                        <div
                                          key={profileIdx}
                                          className="bg-black/30 border border-green-500/20 rounded-lg p-4 hover:border-green-500/40 transition-all"
                                        >
                                          <div className="flex gap-4">
                                            {/* Face Thumbnail */}
                                            <div className="flex-shrink-0">
                                              <img
                                                src={profile.face_url}
                                                alt="Face"
                                                className="w-16 h-16 rounded-lg object-cover border border-green-500/30"
                                                onError={(e) => {
                                                  (e.target as HTMLImageElement).src = '/placeholder-face.png'
                                                }}
                                              />
                                            </div>

                                            {/* Profile Info */}
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center justify-between mb-2">
                                                <h5 className="text-white font-medium truncate">
                                                  {profile.first_name} {profile.last_name}
                                                  {profile.maiden_name && (
                                                    <span className="text-gray-500 text-sm"> ({profile.maiden_name})</span>
                                                  )}
                                                </h5>
                                                <Badge className={getScoreBadgeColor(profile.score)}>
                                                  {profile.score.toFixed(1)}%
                                                </Badge>
                                              </div>

                                              <div className="space-y-1 text-sm">
                                                {(profile.city || profile.country) && (
                                                  <div className="flex items-center gap-2 text-gray-400">
                                                    <MapPin className="w-3 h-3" />
                                                    <span className="truncate">
                                                      {[profile.city, profile.country].filter(Boolean).join(', ')}
                                                    </span>
                                                  </div>
                                                )}
                                                {profile.age && (
                                                  <div className="flex items-center gap-2 text-gray-400">
                                                    <Calendar className="w-3 h-3" />
                                                    <span>{profile.age} years old</span>
                                                  </div>
                                                )}
                                              </div>

                                              <div className="mt-3 flex gap-2">
                                                <a
                                                  href={profile.profile_url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-green-400 hover:text-green-300 text-xs flex items-center gap-1"
                                                >
                                                  <User className="w-3 h-3" />
                                                  Profile
                                                </a>
                                                <a
                                                  href={profile.source_url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-green-400 hover:text-green-300 text-xs flex items-center gap-1"
                                                >
                                                  <ExternalLink className="w-3 h-3" />
                                                  Source Image
                                                </a>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </TabsContent>
                        ))}
                      </Tabs>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Disclaimer */}
        <Card className="bg-yellow-500/10 border border-yellow-500/30">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-yellow-400 font-medium mb-1">Important Notice</p>
              <p className="text-gray-400">
                This tool is intended for legitimate OSINT investigations only. Users are responsible for ensuring 
                their use complies with applicable laws and regulations. Search results may include publicly 
                available information from social media platforms.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
